import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { request } from "../../utils/api";
import Table from "../../components/UI/Table";
import {
  PAGE_HEADER_ROW,
  PAGE_SHELL,
  PAGE_SUBTITLE,
  BTN_PRIMARY,
  BTN_ROW,
  CELL,
  CELL_STRONG,
} from "../../ui/pageChrome";
import { SUBSCRIPTION_PAGE, SUBSCRIPTION_THEME } from "../subscriptionTheme";
import { PayrollIcon } from "../../Icons";
import { PlanEditorModal, AssignedPlanEditorModal } from "./PlanEditors";
import {
  emptyPlanForm,
  formToPlanPayload,
  planToForm,
  snapshotToForm,
  chargeModeFromFlags,
} from "./planFormShared";
import { useUrlTab } from "../../hooks/useUrlTab";

const TAB = {
  plans: "plans",
  assigned: "assigned",
};

function unwrapList(result, key) {
  if (!result) return [];
  if (Array.isArray(result[key])) return result[key];
  if (Array.isArray(result)) return result;
  return [];
}

const PlatformBilling = () => {
  const [tab, setTab] = useUrlTab([TAB.plans, TAB.assigned], TAB.plans);
  const [plans, setPlans] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planModal, setPlanModal] = useState(null);
  const [assignModal, setAssignModal] = useState(null);

  const loadPlans = useCallback(async () => {
    const response = await request("/plans?page=1&limit=100");
    if (response?.status === 1) {
      setPlans(unwrapList(response.result, "plans"));
    }
  }, []);

  const loadAssigned = useCallback(async () => {
    const response = await request("/tenant-plans?page=1&limit=200");
    if (response?.status === 1) {
      setAssigned(unwrapList(response.result, "tenantPlans"));
    }
  }, []);

  const loadTenants = useCallback(async () => {
    const response = await request("/tenants?page=1&limit=200");
    if (response?.status === 1) {
      setTenants(unwrapList(response.result, "tenants"));
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadPlans(), loadAssigned(), loadTenants()]);
    } catch {
      toast.error("Failed to load billing plans");
    } finally {
      setLoading(false);
    }
  }, [loadAssigned, loadPlans, loadTenants]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreatePlan = () => setPlanModal({ mode: "create", form: emptyPlanForm() });
  const openEditPlan = (plan) => setPlanModal({ mode: "edit", id: plan.id, form: planToForm(plan) });

  const openAssign = () =>
    setAssignModal({
      mode: "create",
      form: {
        ...emptyPlanForm(),
        tenantId: "",
        planId: "",
        startDate: new Date().toISOString().slice(0, 10),
        endDate: "",
        notes: "",
        status: "ACTIVE",
        refreshSnapshotFromPlan: false,
      },
    });

  const openEditAssigned = (row) => {
    const snapshot = row.planSnapshot || {};
    const fallback = row.plan || {};
    setAssignModal({
      mode: "edit",
      id: row.id,
      form: {
        ...snapshotToForm(snapshot, fallback),
        tenantId: String(row.tenantId || ""),
        planId: String(row.planId || ""),
        startDate: row.startDate || "",
        endDate: row.endDate || "",
        notes: row.notes || "",
        status: row.status || "ACTIVE",
        billingChargeMode:
          snapshot.billingChargeMode ||
          fallback.billingChargeMode ||
          chargeModeFromFlags(snapshot.chargeEmployees, snapshot.chargePatients),
        refreshSnapshotFromPlan: false,
      },
    });
  };

  const savePlan = async () => {
    if (!planModal) return;
    const payload = formToPlanPayload(planModal.form);
    if (!payload.name) {
      toast.error("Plan name is required");
      return;
    }
    setSaving(true);
    try {
      const isEdit = planModal.mode === "edit";
      const body = isEdit
        ? { ...payload, syncActiveAssignments: Boolean(planModal.form.syncActiveAssignments) }
        : payload;
      const response = await request(isEdit ? `/plans/${planModal.id}` : "/plans", {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify(body),
      });
      if (response?.status === 1) {
        const synced = response.result?.syncedAssignments;
        toast.success(
          synced
            ? `Plan saved and ${synced} assigned plan(s) updated`
            : "Plan saved",
        );
        setPlanModal(null);
        await reload();
      } else {
        toast.error(response?.message || "Failed to save plan");
      }
    } catch (err) {
      toast.error(err.message || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const saveAssigned = async () => {
    if (!assignModal) return;
    const pricing = formToPlanPayload(assignModal.form);
    const tenantId = Number(assignModal.form.tenantId);
    const planId = Number(assignModal.form.planId);
    if (!tenantId || !planId) {
      toast.error("Tenant and plan are required");
      return;
    }
    setSaving(true);
    try {
      const isEdit = assignModal.mode === "edit";
      const body = isEdit
        ? {
            planId,
            startDate: assignModal.form.startDate,
            endDate: assignModal.form.endDate || null,
            status: assignModal.form.status,
            notes: assignModal.form.notes || null,
            refreshSnapshotFromPlan: Boolean(assignModal.form.refreshSnapshotFromPlan),
            ...pricing,
          }
        : {
            tenantId,
            planId,
            startDate: assignModal.form.startDate,
            endDate: assignModal.form.endDate || null,
            notes: assignModal.form.notes || null,
            ...pricing,
          };
      const response = await request(
        isEdit ? `/tenant-plans/${assignModal.id}` : "/tenant-plans",
        {
          method: isEdit ? "PUT" : "POST",
          body: JSON.stringify(body),
        },
      );
      if (response?.status === 1) {
        toast.success(isEdit ? "Assigned plan updated" : "Plan assigned");
        setAssignModal(null);
        await reload();
      } else {
        toast.error(response?.message || "Failed to save assigned plan");
      }
    } catch (err) {
      toast.error(err.message || "Failed to save assigned plan");
    } finally {
      setSaving(false);
    }
  };

  const planColumns = [
    {
      header: "Plan",
      accessor: "name",
      render: (row) => <span className={CELL_STRONG}>{row.name}</span>,
    },
    {
      header: "Bills",
      accessor: "billingChargeMode",
      render: (row) => (
        <span className={CELL}>
          {row.billingChargeMode ||
            chargeModeFromFlags(row.chargeEmployees, row.chargePatients)}
        </span>
      ),
    },
    {
      header: "Employees",
      accessor: "includedUsers",
      render: (row) =>
        row.chargeEmployees === false ? (
          <span className={CELL}>—</span>
        ) : (
          <span className={CELL}>
            {row.includedUsers} incl · ${Number(row.baseAmount || 0).toFixed(2)} basic · $
            {Number(row.pricePerExtraUser || 0).toFixed(2)} extra
          </span>
        ),
    },
    {
      header: "Patients",
      accessor: "includedPatients",
      render: (row) =>
        row.chargePatients ? (
          <span className={CELL}>
            {row.includedPatients} incl · ${Number(row.patientBaseAmount || 0).toFixed(2)} basic · $
            {Number(row.pricePerExtraPatient || 0).toFixed(2)} extra
          </span>
        ) : (
          <span className={CELL}>—</span>
        ),
    },
    {
      header: "Actions",
      accessor: "actions",
      render: (row) => (
        <button type="button" className={BTN_ROW} onClick={() => openEditPlan(row)}>
          Edit
        </button>
      ),
    },
  ];

  const assignedColumns = [
    {
      header: "Tenant",
      accessor: "tenantId",
      render: (row) => (
        <span className={CELL_STRONG}>
          {row.tenant?.companyName || row.tenant?.name || `Tenant #${row.tenantId}`}
        </span>
      ),
    },
    {
      header: "Plan",
      accessor: "planId",
      render: (row) => (
        <span className={CELL}>{row.plan?.name || row.planSnapshot?.name || `#${row.planId}`}</span>
      ),
    },
    {
      header: "Bills",
      accessor: "mode",
      render: (row) => (
        <span className={CELL}>
          {row.planSnapshot?.billingChargeMode ||
            chargeModeFromFlags(row.planSnapshot?.chargeEmployees, row.planSnapshot?.chargePatients)}
        </span>
      ),
    },
    {
      header: "Employees",
      accessor: "emp",
      render: (row) => {
        const snap = row.planSnapshot || {};
        if (snap.chargeEmployees === false) return <span className={CELL}>—</span>;
        return (
          <span className={CELL}>
            {snap.includedUsers ?? 0} incl · ${Number(snap.baseAmount || 0).toFixed(2)} basic · $
            {Number(snap.pricePerExtraUser || 0).toFixed(2)} extra
          </span>
        );
      },
    },
    {
      header: "Patients",
      accessor: "pat",
      render: (row) => {
        const snap = row.planSnapshot || {};
        if (!snap.chargePatients) return <span className={CELL}>—</span>;
        return (
          <span className={CELL}>
            {snap.includedPatients ?? 0} incl · ${Number(snap.patientBaseAmount || 0).toFixed(2)} basic · $
            {Number(snap.pricePerExtraPatient || 0).toFixed(2)} extra
          </span>
        );
      },
    },
    {
      header: "Period",
      accessor: "startDate",
      render: (row) => (
        <span className={`${CELL} tabular-nums`}>
          {row.startDate || "—"}
          {row.endDate ? ` → ${row.endDate}` : ""}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      render: (row) => (
        <button type="button" className={BTN_ROW} onClick={() => openEditAssigned(row)}>
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={SUBSCRIPTION_PAGE.title}>Subscription Plans</h1>
          <p className={PAGE_SUBTITLE}>
            SuperAdmin: edit catalog plans and production assigned plans. Employee and patient
            pricing stay in separate sections.
          </p>
        </div>
        <div className="flex gap-2">
          {tab === TAB.plans ? (
            <button type="button" className={BTN_PRIMARY} onClick={openCreatePlan}>
              New plan
            </button>
          ) : (
            <button type="button" className={BTN_PRIMARY} onClick={openAssign}>
              Assign plan
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[
          { id: TAB.plans, label: "Catalog plans" },
          { id: TAB.assigned, label: "Assigned plans" },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`h-9 px-3 text-sm font-medium border-b-2 ${
              tab === item.id
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className={SUBSCRIPTION_PAGE.card}>
        <div className={`flex items-center gap-2 h-11 px-4 border-b box-border ${SUBSCRIPTION_THEME.section.head}`}>
          <span className={`w-0.5 h-4 rounded-full shrink-0 ${SUBSCRIPTION_THEME.section.bar}`} />
          <span className={`shrink-0 ${SUBSCRIPTION_THEME.section.icon}`}>
            <PayrollIcon size={14} />
          </span>
          <h2 className={`text-xs font-semibold uppercase tracking-wide ${SUBSCRIPTION_THEME.section.title}`}>
            {tab === TAB.plans ? "Plans" : "Assigned plans"}
          </h2>
        </div>
        <Table
          columns={tab === TAB.plans ? planColumns : assignedColumns}
          data={tab === TAB.plans ? plans : assigned}
          loading={loading}
          showSrNo
          density="compact"
          embedded
          headTone={SUBSCRIPTION_THEME.tableHead}
          noDataMessage={tab === TAB.plans ? "No plans yet." : "No assigned plans yet."}
        />
      </div>

      {planModal ? (
        <PlanEditorModal
          title={planModal.mode === "edit" ? "Edit plan" : "Create plan"}
          form={planModal.form}
          setForm={(updater) =>
            setPlanModal((prev) => ({
              ...prev,
              form: typeof updater === "function" ? updater(prev.form) : updater,
            }))
          }
          saving={saving}
          isEdit={planModal.mode === "edit"}
          onClose={() => setPlanModal(null)}
          onSave={savePlan}
        />
      ) : null}

      {assignModal ? (
        <AssignedPlanEditorModal
          title={assignModal.mode === "edit" ? "Edit assigned plan" : "Assign plan"}
          form={assignModal.form}
          setForm={(updater) =>
            setAssignModal((prev) => ({
              ...prev,
              form: typeof updater === "function" ? updater(prev.form) : updater,
            }))
          }
          saving={saving}
          isEdit={assignModal.mode === "edit"}
          plans={plans}
          tenants={tenants}
          onClose={() => setAssignModal(null)}
          onSave={saveAssigned}
        />
      ) : null}
    </div>
  );
};

export default PlatformBilling;
